import { supabase, getStoragePublicUrl } from "../../lib/supabaseClient";
import { mapImageRow } from "./mappers";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

export async function GetUserImages(userId) {
  const { data, error } = await requireClient()
    .from("profile_images")
    .select("*")
    .eq("user_id", userId)
    .order("sort_order");
  if (error) throw error;
  return (data || []).map((row) => ({
    ...mapImageRow(row),
    url: getStoragePublicUrl(row.storage_path),
  }));
}

function isExternalOrLetterAvatar(uri) {
  if (!uri || typeof uri !== "string") return true;
  const lower = uri.toLowerCase();
  return (
    lower.includes("ui-avatars.com") ||
    lower.includes("googleusercontent.com") ||
    lower.includes("ggpht.com") ||
    lower.includes("gravatar.com") ||
    lower.includes("via.placeholder")
  );
}

/** Set primary avatar to the uploaded photo; replace Google/letter placeholders. */
async function syncProfileImageUriFromUpload(userId, storagePath) {
  const publicUrl = getStoragePublicUrl(storagePath);
  if (!publicUrl) return;

  const { data: profile } = await requireClient()
    .from("profiles")
    .select("profile_image_uri")
    .eq("id", userId)
    .maybeSingle();

  const current = profile?.profile_image_uri;
  if (current && !isExternalOrLetterAvatar(current)) return;

  await requireClient()
    .from("profiles")
    .update({ profile_image_uri: publicUrl })
    .eq("id", userId);
}

export async function UploadImages(formData) {
  const userId = formData.get("UserId") ?? formData.get("userId");
  const file = formData.get("imageCollection") ?? formData.get("file");
  if (!userId || !file) throw new Error("UserId and image file required");

  const ext = file.name?.split(".").pop() || "jpg";
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadError } = await requireClient()
    .storage.from("profile-images")
    .upload(storagePath, file, { upsert: false, contentType: file.type || "image/jpeg" });
  if (uploadError) throw uploadError;

  const { count } = await requireClient()
    .from("profile_images")
    .select("*", { count: "exact", head: true })
    .eq("user_id", userId);

  const { data, error } = await requireClient()
    .from("profile_images")
    .insert({
      user_id: userId,
      storage_path: storagePath,
      file_name: fileName,
      sort_order: count ?? 0,
    })
    .select("*")
    .single();
  if (error) throw error;

  await syncProfileImageUriFromUpload(userId, storagePath);

  return mapImageRow(data);
}

export async function UploadProfileAvatar(userId, file) {
  if (!userId || !file) throw new Error("UserId and image file required");

  const ext = file.name?.split(".").pop() || "jpg";
  const fileName = `avatar-${Date.now()}.${ext}`;
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadError } = await requireClient()
    .storage.from("profile-images")
    .upload(storagePath, file, { upsert: true, contentType: file.type || "image/jpeg" });
  if (uploadError) throw uploadError;

  const publicUrl = getStoragePublicUrl(storagePath);

  const { error: profileError } = await requireClient()
    .from("profiles")
    .update({ profile_image_uri: publicUrl })
    .eq("id", userId);
  if (profileError) throw profileError;

  return { profileImageUri: publicUrl, storagePath };
}

export async function RemoveImage(imageId) {
  const { data: row, error: fetchError } = await requireClient()
    .from("profile_images")
    .select("*")
    .eq("id", imageId)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!row) return { success: true };

  await requireClient().storage.from("profile-images").remove([row.storage_path]);
  const { error } = await requireClient().from("profile_images").delete().eq("id", imageId);
  if (error) throw error;
  return { success: true };
}
